import { StorageHelper } from "../../../utilities/StorageHelper";
import { contentManager } from "../../../src/taskpane/config/constants";
import { ClientTypeContext } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";

let cachedRaw: string | null | undefined = undefined; // undefined = not yet read
let targetDirectoryId: string | null = null;
let consumed = false;
// Signature of the fast (schema-synthesized) query used for grid fetch #1, so the
// reconcile can re-fetch only when the authoritative query differs (a token
// correction). null = no fast path ran (OneDrive / no early schema).
let fastQuerySignature: string | null = null;
// The client-type context the fast path synthesized under, stored WITH the signature: the
// async client-type lookup can settle between the fast synthesize and the reconcile, and a
// reconcile evaluating a fresher context would compute different datasource verdicts, a
// different signature, and a spurious corrective grid re-fetch (the double-load bug class).
// The reconcile reuses this snapshot instead - fail-open by design (the restriction lands on
// the next build), symmetric by construction. null = no fast path ran.
let fastClientTypes: ClientTypeContext | null = null;

// Change notification. hasPendingDeepLink() gates the filter panel's section
// build, but this store lives outside redux — a consumption that isn't paired
// with a redux dispatch would otherwise leave the panel's Loader up forever
// (nothing re-renders or re-runs its build effect). The notification lives in
// the MUTATORS, not the call sites, so every present and future consumer of
// markConsumed()/bindTargetDirectoryId() releases subscribers by construction.
// Mutators are only called from effects and handlers (never during render), so
// notifying synchronously here is safe.
let version = 0;
const listeners = new Set<() => void>();
const notifyChanged = (): void => {
  version++;
  listeners.forEach(listener => listener());
};

export const preselectFilterStore = {
  read(): string | null {
    if (cachedRaw === undefined) {
      cachedRaw = StorageHelper.getItem(contentManager.params.preselectFilters) ?? null;
      StorageHelper.removeItem(contentManager.params.preselectFilters);
    }
    return cachedRaw;
  },
  bindTargetDirectoryId(directoryId: string): void {
    if (!targetDirectoryId && directoryId) {
      targetDirectoryId = directoryId;
      notifyChanged();
    }
  },
  getTargetDirectoryId(): string | null {
    return targetDirectoryId;
  },
  markConsumed(): void {
    if (!consumed) {
      consumed = true;
      notifyChanged();
    }
  },
  isConsumed(): boolean {
    return consumed;
  },
  // True while an unconsumed deep link exists AND `directoryId` is (or could
  // still become) its target — the window in which the reconcile
  // (usePreselectFilters) owns section building for that directory. Other
  // builders (the filter panel's own build) must stand down in this window or
  // they race the reconcile and can pre-empt the deep link with an un-kept,
  // unselected section set. The check MUST be directory-scoped: the target
  // binding is permanent, the deep link can never apply anywhere else, and an
  // unscoped check blocks the panel build in every other directory for the
  // whole session (the reconcile deliberately never consumes on a foreign-
  // directory abandon, so nothing would ever unblock it).
  hasPendingDeepLink(directoryId?: string): boolean {
    if (this.read() === null || consumed) {
      return false;
    }
    // Not yet bound (boot window): stand down conservatively — the fast path /
    // reconcile binds the target before any competing build matters.
    return targetDirectoryId === null || targetDirectoryId === directoryId;
  },
  // Terminal give-up: the deep link's directory can no longer produce a
  // reconcile (its fetch failed terminally, settled without a stream, or
  // settled with no usable field source), so nothing will ever consume the
  // link — consume it here or the panel's build gate never releases. Scoped
  // through hasPendingDeepLink so a FOREIGN directory's failure cannot kill a
  // deep link that hasn't reached its target yet. Returns whether it consumed,
  // so callers can pair their redux clears (e.g. the pending flag).
  abandonPendingDeepLink(directoryId?: string): boolean {
    if (!this.hasPendingDeepLink(directoryId)) {
      return false;
    }
    this.markConsumed();
    return true;
  },
  setFastQuerySignature(signature: string, clientTypes?: ClientTypeContext): void {
    fastQuerySignature = signature;
    fastClientTypes = clientTypes ?? null;
  },
  getFastQuerySignature(): string | null {
    return fastQuerySignature;
  },
  /** The context grid fetch #1 was built under, for the reconcile to reuse (signature
   * parity). null when no fast path ran - the reconcile then uses its own live context. */
  getFastClientTypes(): ClientTypeContext | null {
    return fastClientTypes;
  },
  /** Invalidate the fast snapshot. MUST be called on every directory change: the signature's
   * validity premise is "grid fetch #1 ran with the fast-synthesized sections", and a
   * navigation clears those sections. A same-list re-entry does NOT re-run the fast path
   * (the [selectedView] effect never re-fires), so a surviving stale signature would make
   * the reconcile compare against a fetch that never happened - suppressing the corrective
   * reload and leaving an UNFILTERED grid under applied-looking filters. */
  clearFastQuery(): void {
    fastQuerySignature = null;
    fastClientTypes = null;
  },
  // Monotonic change counter for the mutations above; lets React consumers
  // subscribe (usePreselectStoreVersion) since this store is not redux-backed.
  getVersion(): number {
    return version;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  __reset(): void {
    cachedRaw = undefined;
    targetDirectoryId = null;
    consumed = false;
    fastQuerySignature = null;
    fastClientTypes = null;
    version = 0;
    listeners.clear();
  },
};
